// EditSectionType — Section 1 of editRealty.
// Substrate: design/editRealty.html lines 225-359 — `<section id="sec-type" class="ap-pane">`.
//
// Backend-truth map:
//   * property_type, deal_type, title, price, currency, has_housing_program,
//     has_recovery_program  → PropertyWriteSerializer (PATCH /properties/{id}/)
//   * discount               → backend-truth-missing (no field on Property model)
//                              Rendered structurally, input disabled, TODO.
//   * cooperation_offer      → DEALS-CREATION-FLOW-B2 nested write on Property
//
// Per CLAUDE.md SR-3 (Topology-Driven Local Styling Is Legitimate): we
// preserve the canonical .radio-card / .toggle-card / .check-card
// wrappers verbatim. No primitive substitution, no .ui-form swap.

import CooperationOfferFields from "../../realty/widgets/CooperationOfferFields";

const PROPERTY_TYPE_OPTIONS = [
  { value: "apartment", label: "Квартира", icon: "fa-solid fa-building", hint: "Житлова" },
  { value: "house", label: "Будинок", icon: "fa-solid fa-house-chimney", hint: "Житлова" },
  { value: "land", label: "Земельна ділянка", icon: "fa-solid fa-mountain-sun", hint: "Земля" },
  { value: "commercial", label: "Комерційна", icon: "fa-solid fa-store", hint: "Комерційна" },
  { value: "industrial", label: "Промислова", icon: "fa-solid fa-industry", hint: "Комерційна" },
];

const DEAL_TYPE_OPTIONS = [
  { value: "sale", label: "Продаж", icon: "fa-solid fa-tag" },
  { value: "rent", label: "Оренда", icon: "fa-solid fa-key" },
];

const CURRENCY_OPTIONS = [
  { value: "USD", label: "USD — Долар" },
  { value: "EUR", label: "EUR — Євро" },
  { value: "UAH", label: "UAH — Гривня" },
];

function EditSectionType({
  values,
  onChange,
  fieldErrors = {},
  propertyTypeLocked = false,
  dealTypeLocked = false,
}) {
  const setField = (key, value) => onChange({ ...values, [key]: value });

  return (
    <section id="sec-type" className="ap-pane" aria-labelledby="ap-pane-type">
      <header className="ap-pane__head">
        <h2 id="ap-pane-type">
          <i className="fa-solid fa-tag" aria-hidden="true" />
          Тип нерухомості та угоди
        </h2>
        <p>Тип об'єкта впливає на склад полів у розділі «Характеристики».</p>
      </header>

      <fieldset className="ap-fieldset">
        <legend>
          Тип нерухомості <span className="req">*</span>
        </legend>
        <div
          className="ap-radio-grid"
          role="radiogroup"
          aria-label="Тип нерухомості"
        >
          {/* RIC-2.2.3 — property_type is immutable after creation (the
              edit surface passes propertyTypeLocked unconditionally).
              Radios render `disabled` so the current value stays visible
              but cannot be changed; switching type would orphan the
              incompatible subtype row. Supersedes the R6.2A draft-only
              lock. */}
          {PROPERTY_TYPE_OPTIONS.map((opt) => (
            <label key={opt.value} className="radio-card">
              <input
                type="radio"
                name="property_type"
                value={opt.value}
                checked={values.property_type === opt.value}
                onChange={() => setField("property_type", opt.value)}
                disabled={propertyTypeLocked}
                aria-disabled={propertyTypeLocked || undefined}
              />
              <span className="radio-card__icon">
                <i className={opt.icon} aria-hidden="true" />
              </span>
              <span className="radio-card__label">{opt.label}</span>
              <span className="radio-card__hint">{opt.hint}</span>
            </label>
          ))}
        </div>
        {propertyTypeLocked ? (
          <small className="form-hint">
            <i className="fa-solid fa-circle-info" aria-hidden="true" />{" "}
            Тип нерухомості зафіксовано після створення оголошення і не може
            бути змінений.
          </small>
        ) : null}
        {fieldErrors.property_type ? (
          <small className="form-hint text-danger">
            {fieldErrors.property_type}
          </small>
        ) : null}
      </fieldset>

      <fieldset className="ap-fieldset">
        <legend>
          Тип угоди <span className="req">*</span>
        </legend>
        <div
          className="ap-toggle-group"
          role="radiogroup"
          aria-label="Тип угоди"
        >
          {/* RIC-2.2.4 — deal_type is immutable after creation. Switching
              sale↔rent changes the listing's business meaning; the edit
              surface passes dealTypeLocked so the value stays visible but
              read-only. */}
          {DEAL_TYPE_OPTIONS.map((opt) => (
            <label key={opt.value} className="toggle-card">
              <input
                type="radio"
                name="deal_type"
                value={opt.value}
                checked={values.deal_type === opt.value}
                onChange={() => setField("deal_type", opt.value)}
                disabled={dealTypeLocked}
                aria-disabled={dealTypeLocked || undefined}
              />
              <i className={opt.icon} aria-hidden="true" />
              {opt.label}
            </label>
          ))}
        </div>
        {dealTypeLocked ? (
          <small className="form-hint">
            <i className="fa-solid fa-circle-info" aria-hidden="true" />{" "}
            Тип угоди зафіксовано після створення оголошення і не може бути
            змінений.
          </small>
        ) : null}
        {fieldErrors.deal_type ? (
          <small className="form-hint text-danger">
            {fieldErrors.deal_type}
          </small>
        ) : null}
      </fieldset>

      <div className="ap-row">
        <div className="ap-field ap-field--wide">
          <label htmlFor="prop-title">
            Заголовок <span className="req">*</span>
          </label>
          <input
            id="prop-title"
            name="title"
            type="text"
            className="form-control"
            maxLength={255}
            required
            value={values.title || ""}
            onChange={(e) => setField("title", e.target.value)}
          />
          <small className="form-hint">
            До 255 символів. Це назва картки на сайті.
          </small>
          {fieldErrors.title ? (
            <small className="form-hint text-danger">{fieldErrors.title}</small>
          ) : null}
        </div>
      </div>

      <div className="ap-row ap-row--2">
        <div className="ap-field">
          <label htmlFor="prop-price">
            Ціна <span className="req">*</span>
          </label>
          <div className="ap-input-group">
            <input
              id="prop-price"
              name="price"
              type="number"
              className="form-control"
              min="0"
              step="0.01"
              required
              value={values.price ?? ""}
              onChange={(e) => setField("price", e.target.value)}
            />
            <select
              id="prop-currency"
              name="currency"
              className="form-select"
              aria-label="Валюта"
              value={values.currency || "USD"}
              onChange={(e) => setField("currency", e.target.value)}
            >
              {CURRENCY_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <small className="form-hint">До 12 цифр, не від'ємна.</small>
          {fieldErrors.price ? (
            <small className="form-hint text-danger">{fieldErrors.price}</small>
          ) : null}
          {fieldErrors.currency ? (
            <small className="form-hint text-danger">
              {fieldErrors.currency}
            </small>
          ) : null}
        </div>
        <div className="ap-field">
          <label htmlFor="prop-discount">Знижка</label>
          {/* TODO: backend-truth-missing — Property model has no
              `discount` field. Slot preserved structurally + disabled
              per R6.1 STEP R6.1.4. */}
          <input
            id="prop-discount"
            name="discount"
            type="number"
            className="form-control"
            min="0"
            value=""
            disabled
            aria-disabled="true"
          />
          <small className="form-hint">
            Поле тимчасово недоступне (немає підтримки на бекенді).
          </small>
        </div>
      </div>

      <fieldset className="ap-fieldset">
        <legend>Державні програми</legend>
        <div className="ap-checkbox-row">
          <label className="check-card">
            <input
              type="checkbox"
              name="has_housing_program"
              checked={Boolean(values.has_housing_program)}
              onChange={(e) =>
                setField("has_housing_program", e.target.checked)
              }
            />
            <span className="check-card__icon">
              <i className="fa-solid fa-house-flag" aria-hidden="true" />
            </span>
            <span className="check-card__body">
              <strong>єОселя</strong>
              <small>Державна житлова програма</small>
            </span>
          </label>
          <label className="check-card">
            <input
              type="checkbox"
              name="has_recovery_program"
              checked={Boolean(values.has_recovery_program)}
              onChange={(e) =>
                setField("has_recovery_program", e.target.checked)
              }
            />
            <span className="check-card__icon">
              <i className="fa-solid fa-rotate" aria-hidden="true" />
            </span>
            <span className="check-card__body">
              <strong>єВідновлення</strong>
              <small>Державна програма відновлення</small>
            </span>
          </label>
        </div>
      </fieldset>

      {/* TODO: backend-truth-missing — discount field does not exist on the
          Property model. Slot preserved structurally; input disabled. */}
      {/* DEALS-CREATION-FLOW-B2 — replaces former disabled MLS stubs with
          real cooperation_offer nested write. */}
      <CooperationOfferFields
        values={values}
        onChange={onChange}
        fieldErrors={fieldErrors}
        idPrefix="edit-coop"
      />
    </section>
  );
}

export default EditSectionType;
